/**
 * ThresholdMonitor — pure functions for detecting IP/CP boundary crossings
 * and generating pending test prompts.
 *
 * All functions are pure: no side effects, no store access.
 */

import { nanoid } from 'nanoid';
import type { MentalDisorder, PendingTest } from '../types/character';

/** 10-point boundaries for IP/CP (10, 20, 30, ..., 90) */
const TEN_POINT_BOUNDARIES = [10, 20, 30, 40, 50, 60, 70, 80, 90];

/** Mutation test thresholds at 30, 60, 90 CP */
const MUTATION_THRESHOLDS = [30, 60, 90];

/** Disorder assignment thresholds mapped to severity */
const DISORDER_THRESHOLDS: { threshold: number; severity: 'minor' | 'severe' | 'acute' }[] = [
  { threshold: 40, severity: 'minor' },
  { threshold: 60, severity: 'severe' },
  { threshold: 80, severity: 'acute' }
];

/**
 * Returns which 10-point IP boundaries are crossed going from oldIP to newIP.
 * A boundary is crossed when old < boundary <= new.
 *
 * @example getIPBoundariesCrossed(8, 32) => [10, 20, 30]
 */
export function getIPBoundariesCrossed(oldIP: number, newIP: number): number[] {
  if (newIP <= oldIP) return [];
  return TEN_POINT_BOUNDARIES.filter((b) => oldIP < b && b <= newIP);
}

/**
 * Returns which 10-point CP boundaries are crossed going from oldCP to newCP.
 * Same logic as IP boundaries.
 *
 * @example getCPBoundariesCrossed(5, 25) => [10, 20]
 */
export function getCPBoundariesCrossed(oldCP: number, newCP: number): number[] {
  if (newCP <= oldCP) return [];
  return TEN_POINT_BOUNDARIES.filter((b) => oldCP < b && b <= newCP);
}

/**
 * Returns which mutation thresholds (30, 60, 90) are crossed going from oldCP to newCP.
 *
 * @example getMutationThresholdsCrossed(25, 65) => [30, 60]
 */
export function getMutationThresholdsCrossed(oldCP: number, newCP: number): number[] {
  if (newCP <= oldCP) return [];
  return MUTATION_THRESHOLDS.filter((t) => oldCP < t && t <= newCP);
}

/**
 * Returns which disorder thresholds (40/60/80) are crossed going from oldIP to newIP,
 * excluding thresholds where a disorder of the matching severity already exists.
 *
 * @example getDisorderThresholdsCrossed(35, 65, []) => [{threshold: 40, severity: 'minor'}, {threshold: 60, severity: 'severe'}]
 */
export function getDisorderThresholdsCrossed(
  oldIP: number,
  newIP: number,
  existingDisorders: MentalDisorder[]
): { threshold: number; severity: 'minor' | 'severe' | 'acute' }[] {
  if (newIP <= oldIP) return [];

  return DISORDER_THRESHOLDS.filter((dt) => {
    const crossed = oldIP < dt.threshold && dt.threshold <= newIP;
    if (!crossed) return false;
    // Skip if a disorder of this severity already exists
    const alreadyHas = existingDisorders.some((d) => d.severity === dt.severity);
    return !alreadyHas;
  });
}

/**
 * Generates pending tests from an IP change:
 * - A trauma-test for each 10-point boundary crossed
 * - A disorder-assignment for each 40/60/80 crossing (if no disorder of that severity exists)
 */
export function generateIPPendingTests(
  oldIP: number,
  newIP: number,
  existingDisorders: MentalDisorder[]
): PendingTest[] {
  const tests: PendingTest[] = [];
  const now = Date.now();

  // Trauma tests for each 10-point boundary
  for (const boundary of getIPBoundariesCrossed(oldIP, newIP)) {
    tests.push({
      id: nanoid(),
      type: 'trauma-test',
      threshold: boundary,
      createdAt: now
    });
  }

  // Disorder assignments for 40/60/80 crossings
  for (const dt of getDisorderThresholdsCrossed(oldIP, newIP, existingDisorders)) {
    tests.push({
      id: nanoid(),
      type: 'disorder-assignment',
      threshold: dt.threshold,
      severity: dt.severity,
      createdAt: now
    });
  }

  return tests;
}

/**
 * Generates pending tests from a CP change:
 * - A malignancy-test for each 10-point boundary crossed
 * - A mutation-test for each 30/60/90 crossing
 */
export function generateCPPendingTests(oldCP: number, newCP: number): PendingTest[] {
  const tests: PendingTest[] = [];
  const now = Date.now();

  // Malignancy tests for each 10-point boundary
  for (const boundary of getCPBoundariesCrossed(oldCP, newCP)) {
    tests.push({
      id: nanoid(),
      type: 'malignancy-test',
      threshold: boundary,
      createdAt: now
    });
  }

  // Mutation tests for 30/60/90 crossings
  for (const threshold of getMutationThresholdsCrossed(oldCP, newCP)) {
    tests.push({
      id: nanoid(),
      type: 'mutation-test',
      threshold,
      createdAt: now
    });
  }

  return tests;
}
